import React from 'react'

import {
  AlignItems,
  Body2,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  FlexDirection,
  Heading6,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'

import { FlexPortionOptions } from './FlexPortionOptions'

// TODO: Replace this fallback with portions returned by the Flex API when it is available.
const DEFAULT_PORTIONS = [
  { label: '1', numPortions: 1, isAvailable: true },
  { label: '2', numPortions: 2, isAvailable: true },
  { label: '3', numPortions: 3, isAvailable: true },
  { label: '4', numPortions: 4, isAvailable: true },
  { label: '5', numPortions: 5, isAvailable: true },
]

type FlexPortionEditorProps = {
  recipeCount: number
  numPortions: number
  portions?: {
    label: string
    numPortions: number
    isAvailable: boolean
  }[]
  onUpdatePortions: (numPortions: number) => void
  onRemoveRecipe: () => void
  onClose: () => void
}

export const FlexPortionEditor = ({
  recipeCount,
  numPortions,
  portions = DEFAULT_PORTIONS,
  onUpdatePortions,
  onRemoveRecipe,
  onClose,
}: FlexPortionEditorProps) => {
  const [selectedNumPortions, setSelectedNumPortions] = React.useState(numPortions)

  React.useEffect(() => {
    setSelectedNumPortions(numPortions)
  }, [numPortions])

  const selectedPortion = portions.find((portion) => portion.numPortions === selectedNumPortions)
  const updateDisabled =
    selectedNumPortions === numPortions || selectedPortion?.isAvailable !== true
  const showRecipeCardInfo = selectedNumPortions === 1 || selectedNumPortions === 5

  return (
    <Box bg={Color.White} data-testid="flex-portion-editor" width="100%">
      <Box
        alignItems={AlignItems.Center}
        borderBottomWidth={0.5}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        paddingH={4}
        paddingV={1}
      >
        <Heading6>{`${recipeCount} ${recipeCount === 1 ? 'recipe' : 'recipes'}`}</Heading6>
        <Button
          aria-label="Close portion editor"
          colorVariant={ButtonColorVariant.Tertiary}
          icon="close"
          onClick={onClose}
          variant={ButtonVariant.Icon}
        />
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} paddingH={4} paddingV={4}>
        <Box
          aria-describedby="flex-portions-description"
          aria-label="Select number of portions"
          role="group"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={4}
          paddingBottom={4}
        >
          <Heading6>How many portions?</Heading6>
          <FlexPortionOptions
            onChange={setSelectedNumPortions}
            portions={portions}
            selectedNumPortions={selectedNumPortions}
          />
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={3}
          paddingH={4}
          paddingBottom={4}
        >
          {showRecipeCardInfo && (
            <Body2 aria-live="polite" size={2}>
              Heads up: When you cook a recipe for {selectedNumPortions}, you&apos;ll need to use
              your Gousto app for cooking instructions.
            </Body2>
          )}
          <Body2 id="flex-portions-description" size={2}>
            We calculate portion prices at checkout based on the total recipes and portions in your
            box.
          </Body2>
        </Box>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
          <Button
            disabled={updateDisabled}
            onClick={() => onUpdatePortions(selectedNumPortions)}
            width="100%"
          >
            Update portions
          </Button>
          <Space direction="vertical" size={2} />
          <Button colorVariant={ButtonColorVariant.Tertiary} onClick={onRemoveRecipe} width="100%">
            <Icon name="delete" variant={IconVariant.Actionable} />
            <Space direction="horizontal" size={1} />
            Remove recipe
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
